import React, { useState } from 'react';
import { 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  Building2,
  Phone,
  Mail,
  MapPin,
  Users,
  Ship,
  User,
  FileText
} from 'lucide-react';

const Clients = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const clients = [
    {
      id: 1,
      customer_code: 'CMA001',
      social_reason: 'CMA CGM',
      siren: '123456789',
      naf: '5010',
      tva_number: 'FR12345678901',
      logo_url: '/logos/cmacgm.png',
      adresse: '4 Quai d\'Arenc',
      postal_code: '13002',
      city: 'Marseille',
      country: 'France',
      code_comptable: 'CMA001',
      notes: 'Client principal - Transport maritime',
      statut: 'Actif',
      date_creation: '2024-01-01',
      nombreLivraisons: 45,
      recipients: [
        {
          id: 1,
          label: 'CMA CGM Marseille',
          imo_number: 'IMO1234567',
          type: 'Company',
          adresse: '4 Quai d\'Arenc',
          postal_code: '13002',
          city: 'Marseille',
          country: 'France'
        }
      ],
      contacts: [
        {
          id: 1,
          name: 'Jean Dupont',
          email: 'contact@cmacgm.com',
          telephone: '+33 1 23 45 67 89'
        }
      ]
    },
    {
      id: 2,
      customer_code: 'MSC002',
      social_reason: 'MSC',
      siren: '987654321',
      naf: '5010',
      tva_number: 'FR98765432109',
      logo_url: '/logos/msc.png',
      adresse: '12 Boulevard Michelet',
      postal_code: '13008',
      city: 'Marseille',
      country: 'France',
      code_comptable: 'MSC002',
      notes: 'Client international',
      statut: 'Actif',
      date_creation: '2024-01-01',
      nombreLivraisons: 38,
      recipients: [
        {
          id: 2,
          label: 'MSC Terminal',
          imo_number: 'IMO7654321',
          type: 'Company',
          adresse: '12 Boulevard Michelet',
          postal_code: '13008',
          city: 'Marseille',
          country: 'France'
        }
      ],
      contacts: [
        {
          id: 2,
          name: 'Marie Martin',
          email: 'info@msc.com',
          telephone: '+33 1 98 76 54 32'
        }
      ]
    },
    {
      id: 3,
      customer_code: 'EVR003',
      social_reason: 'Evergreen',
      siren: '111222333',
      naf: '5010',
      tva_number: 'FR11122233344',
      logo_url: '/logos/evergreen.png',
      adresse: '8 Rue de la République',
      postal_code: '13001',
      city: 'Marseille',
      country: 'France',
      code_comptable: 'EVR003',
      notes: 'Client asiatique',
      statut: 'Actif',
      date_creation: '2024-01-01',
      nombreLivraisons: 22,
      recipients: [
        {
          id: 3,
          label: 'Evergreen Marseille',
          imo_number: 'IMO1112223',
          type: 'Company',
          adresse: '8 Rue de la République',
          postal_code: '13001',
          city: 'Marseille',
          country: 'France'
        }
      ],
      contacts: [
        {
          id: 3,
          name: 'Pierre Durand',
          email: 'contact@evergreen.com',
          telephone: '+33 1 11 22 33 44'
        }
      ]
    }
  ];

  const filteredClients = clients.filter(client =>
    client.social_reason.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.customer_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.siren.includes(searchTerm) ||
    (client.contacts && client.contacts.some(contact => 
      contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchTerm.toLowerCase())
    ))
  );

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-port-gray-900">Gestion des clients</h1>
          <p className="text-port-gray-600">Gestion des clients ATL</p>
        </div>
        <button 
          onClick={() => window.location.href = '/nouveau-client'}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus className="h-5 w-5" />
          <span>Nouveau client</span>
        </button>
      </div>

      {/* Recherche */}
      <div className="card p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-port-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par raison sociale, code client, SIREN, contact..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Tableau des clients */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-port-gray-200">
            <thead className="bg-port-gray-50">
              <tr>
                <th className="table-header px-6 py-3 text-left">Client</th>
                <th className="table-header px-6 py-3 text-left">Informations légales</th>
                <th className="table-header px-6 py-3 text-left">Adresse</th>
                <th className="table-header px-6 py-3 text-left">Contacts</th>
                <th className="table-header px-6 py-3 text-left">Destinataires</th>
                <th className="table-header px-6 py-3 text-left">Statut</th>
                <th className="table-header px-6 py-3 text-left">Livraisons</th>
                <th className="table-header px-6 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-port-gray-200">
              {filteredClients.map((client) => (
                <tr key={client.id} className="table-row">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {client.logo_url ? (
                        <img src={client.logo_url} alt={client.social_reason} className="h-10 w-10 rounded mr-3" />
                      ) : (
                        <Building2 className="h-10 w-10 text-atl-blue-600 mr-3" />
                      )}
                      <div>
                        <div className="font-medium text-port-gray-900">{client.social_reason}</div>
                        <div className="text-sm text-port-gray-500">Code: {client.customer_code}</div>
                        <div className="text-sm text-port-gray-500">Code comptable: {client.code_comptable}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1 text-sm">
                      <div className="text-port-gray-900">
                        <span className="font-medium">SIREN:</span> {client.siren}
                      </div>
                      <div className="text-port-gray-900">
                        <span className="font-medium">NAF:</span> {client.naf}
                      </div>
                      <div className="text-port-gray-900">
                        <span className="font-medium">TVA:</span> {client.tva_number}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1 text-sm">
                      <div className="text-port-gray-900">{client.adresse}</div>
                      <div className="text-port-gray-600">
                        {client.postal_code} {client.city}
                      </div>
                      <div className="text-port-gray-600">{client.country}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-2">
                      {client.contacts && client.contacts.map((contact) => (
                        <div key={contact.id} className="text-sm">
                          <div className="font-medium text-port-gray-900">{contact.name}</div>
                          <div className="flex items-center text-port-gray-600">
                            <Mail className="h-3 w-3 mr-1" />
                            {contact.email}
                          </div>
                          <div className="flex items-center text-port-gray-600">
                            <Phone className="h-3 w-3 mr-1" />
                            {contact.telephone}
                          </div>
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-2">
                      {client.recipients && client.recipients.map((recipient) => (
                        <div key={recipient.id} className="text-sm">
                          <div className="flex items-center">
                            {recipient.type === 'Ship' ? (
                              <Ship className="h-4 w-4 text-atl-blue-600 mr-2" />
                            ) : (
                              <Building2 className="h-4 w-4 text-atl-green-600 mr-2" />
                            )}
                            <span className="font-medium text-port-gray-900">{recipient.label}</span>
                          </div>
                          {recipient.imo_number && (
                            <div className="text-port-gray-600 text-xs">IMO: {recipient.imo_number}</div>
                          )}
                          <div className="text-port-gray-600 text-xs">
                            {recipient.city}, {recipient.country}
                          </div>
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      client.statut === 'Actif' ? 'bg-atl-green-100 text-atl-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {client.statut}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-port-gray-900">{client.nombreLivraisons}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <button 
                        onClick={() => window.location.href = `/detail-client/${client.id}`}
                        className="text-atl-blue-600 hover:text-atl-blue-700"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={() => window.location.href = `/modifier-client/${client.id}`}
                        className="text-port-gray-600 hover:text-port-gray-700"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button className="text-red-600 hover:text-red-700">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Clients; 